(function (globalScope) {
  const SERVICE_FLAG_PROPERTY = '_is_collection_service';
  const SERVICE_ID_PROPERTY = '_collection_service_id';
  const SERVICE_FLAG_VALUE = 'true';
  const RULES_SELECTOR = '[data-collection-service-rules]';
  const CART_REFRESH_EVENT = 'theme:cart:refresh';
  const CART_CHANGE_PATH = '/cart/change';
  const CART_UPDATE_PATH = '/cart/update';
  const JSON_CONTENT_TYPE = 'application/json';

  let validationPromise = null;
  let validationRequested = false;
  let pendingCartPromise = null;
  let isApplyingCartActions = false;

  function normalizePositiveInteger(value) {
    const normalizedValue = Number.parseInt(value, 10);

    if (!Number.isFinite(normalizedValue) || normalizedValue < 1) {
      return 0;
    }

    return normalizedValue;
  }

  function normalizeStringArray(value) {
    if (!Array.isArray(value)) {
      return [];
    }

    return Array.from(
      new Set(
        value
          .map((item) => (typeof item === 'string' ? item.trim() : ''))
          .filter(Boolean)
      )
    );
  }

  function normalizeRules(rules) {
    if (!Array.isArray(rules)) {
      return [];
    }

    return rules
      .map((rule) => {
        const variantId = normalizePositiveInteger(rule?.variantId);
        const productId = normalizePositiveInteger(rule?.productId);
        const collectionHandles = normalizeStringArray(rule?.collectionHandles);
        const id = typeof rule?.id === 'string' && rule.id.trim() ? rule.id.trim() : `collection-service-${variantId}`;

        return {
          id,
          productId,
          variantId,
          collectionHandles,
        };
      })
      .filter((rule) => rule.variantId > 0 && rule.collectionHandles.length > 0);
  }

  function readRules(root = document) {
    const script = root?.querySelector?.(RULES_SELECTOR);

    if (!script) {
      return [];
    }

    try {
      return normalizeRules(JSON.parse(script.textContent || '[]'));
    } catch (error) {
      console.error('Collection Service Rules Error:', error);
      return [];
    }
  }

  function splitCollectionHandles(value) {
    if (typeof value !== 'string') {
      return [];
    }

    return normalizeStringArray(value.split('|'));
  }

  function getLineProperties(item) {
    return item?.properties && typeof item.properties === 'object' ? item.properties : {};
  }

  function isCollectionServiceLine(item) {
    return getLineProperties(item)[SERVICE_FLAG_PROPERTY] === SERVICE_FLAG_VALUE;
  }

  function getCollectionServiceId(item) {
    const serviceId = getLineProperties(item)[SERVICE_ID_PROPERTY];

    return typeof serviceId === 'string' ? serviceId : '';
  }

  function parseLineMetadataFromElement(element) {
    return {
      key: element.getAttribute('data-item') || '',
      productId: normalizePositiveInteger(element.getAttribute('data-product-id')),
      variantId: normalizePositiveInteger(element.getAttribute('data-variant-id')),
      collectionHandles: splitCollectionHandles(element.getAttribute('data-collection-service-collections')),
      isCollectionService: element.getAttribute('data-collection-service-line') === 'true',
      serviceId: element.getAttribute('data-collection-service-id') || '',
    };
  }

  function buildLineMetadataMap(root) {
    const metadataMap = {};

    root?.querySelectorAll?.('[data-item]').forEach((element) => {
      const metadata = parseLineMetadataFromElement(element);

      if (metadata.key) {
        metadataMap[metadata.key] = metadata;
      }
    });

    return metadataMap;
  }

  function buildCartLines(cart, metadataMap = {}) {
    return (cart?.items || []).map((item) => {
      const metadata = metadataMap[item.key] || {};
      const productId = normalizePositiveInteger(item.product_id || metadata.productId);
      const variantId = normalizePositiveInteger(item.variant_id || item.id || metadata.variantId);

      return {
        key: item.key,
        productId,
        variantId,
        quantity: normalizePositiveInteger(item.quantity),
        properties: getLineProperties(item),
        collectionHandles: metadata.collectionHandles || [],
        isCollectionService: isCollectionServiceLine(item) || metadata.isCollectionService === true,
        serviceId: getCollectionServiceId(item) || metadata.serviceId || '',
      };
    });
  }

  function lineMatchesRule(line, rule) {
    if (!line || line.isCollectionService) {
      return false;
    }

    if (rule.productId > 0 && line.productId === rule.productId) {
      return false;
    }

    return line.collectionHandles.some((collectionHandle) => rule.collectionHandles.includes(collectionHandle));
  }

  function computeDesiredServiceQuantities(lines, rules) {
    const desiredQuantities = {};

    rules.forEach((rule) => {
      desiredQuantities[rule.id] = 0;
    });

    lines.forEach((line) => {
      rules.forEach((rule) => {
        if (lineMatchesRule(line, rule)) {
          desiredQuantities[rule.id] += line.quantity;
        }
      });
    });

    return desiredQuantities;
  }

  function buildServiceProperties(rule) {
    return {
      [SERVICE_FLAG_PROPERTY]: SERVICE_FLAG_VALUE,
      [SERVICE_ID_PROPERTY]: rule.id,
    };
  }

  function computeCollectionServiceActions({ cart, metadataMap = {}, rules = [] } = {}) {
    const normalizedRules = normalizeRules(rules);
    const lines = buildCartLines(cart, metadataMap);
    const desiredQuantities = computeDesiredServiceQuantities(lines, normalizedRules);
    const updates = {};
    const additions = [];

    normalizedRules.forEach((rule) => {
      const desiredQuantity = desiredQuantities[rule.id] || 0;
      const serviceLines = lines.filter((line) => line.isCollectionService && line.serviceId === rule.id);

      if (desiredQuantity > 0 && serviceLines.length === 0) {
        additions.push({
          id: rule.variantId,
          quantity: desiredQuantity,
          properties: buildServiceProperties(rule),
        });
        return;
      }

      serviceLines.forEach((serviceLine, index) => {
        const nextQuantity = index === 0 ? desiredQuantity : 0;

        if (serviceLine.key && serviceLine.quantity !== nextQuantity) {
          updates[serviceLine.key] = nextQuantity;
        }
      });
    });

    return { additions, updates };
  }

  function hasActions(actions) {
    return Boolean(actions?.additions?.length) || Object.keys(actions?.updates || {}).length > 0;
  }

  function getRootRoute() {
    return globalScope.Shopify?.routes?.root || globalScope.theme?.routes?.root || '/';
  }

  function getCartUrl() {
    return globalScope.theme?.routes?.cart_url || `${getRootRoute()}cart`;
  }

  async function fetchCart() {
    const response = await fetch(`${getRootRoute()}cart.js`);
    return response.json();
  }

  async function fetchLineMetadataMap() {
    const response = await fetch(`${getCartUrl()}?section_id=api-cart-items`);
    const html = await response.text();
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');

    return buildLineMetadataMap(documentFragment);
  }

  async function applyCartUpdates(updates) {
    await fetch(`${getRootRoute()}cart/update.js`, {
      method: 'POST',
      headers: {
        'Content-Type': JSON_CONTENT_TYPE,
        Accept: 'application/json',
      },
      body: JSON.stringify({ updates }),
    });
  }

  async function applyCartAdditions(additions) {
    await fetch(`${getRootRoute()}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': JSON_CONTENT_TYPE,
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({ items: additions }),
    });
  }

  async function applyCartActions(actions) {
    if (!hasActions(actions)) {
      return;
    }

    isApplyingCartActions = true;

    try {
      if (Object.keys(actions.updates).length > 0) {
        await applyCartUpdates(actions.updates);
      }

      if (actions.additions.length > 0) {
        await applyCartAdditions(actions.additions);
      }
    } finally {
      isApplyingCartActions = false;
    }

    document.dispatchEvent(new CustomEvent(CART_REFRESH_EVENT, { bubbles: true }));
  }

  async function validateCollectionServiceCart(cart = null) {
    try {
      const rules = readRules();

      if (rules.length === 0) {
        return;
      }

      const [resolvedCart, metadataMap] = await Promise.all([cart || fetchCart(), fetchLineMetadataMap()]);
      const actions = computeCollectionServiceActions({
        cart: resolvedCart,
        metadataMap,
        rules,
      });

      await applyCartActions(actions);
    } catch (error) {
      console.error('Collection Service Cart Validation Error:', error);
    }
  }

  function requestCartValidation(cartPromise = null) {
    validationRequested = true;

    if (cartPromise) {
      pendingCartPromise = cartPromise;
    }

    if (validationPromise) {
      return validationPromise;
    }

    validationPromise = (async () => {
      while (validationRequested) {
        validationRequested = false;
        const nextCartPromise = pendingCartPromise;
        pendingCartPromise = null;
        const nextCart = nextCartPromise ? await nextCartPromise : null;
        await validateCollectionServiceCart(nextCart);
      }
    })().finally(() => {
      validationPromise = null;
    });

    return validationPromise;
  }

  function shouldValidateCart(responseUrl) {
    return (
      responseUrl.includes('/cart/add') ||
      responseUrl.includes(CART_CHANGE_PATH) ||
      responseUrl.includes(CART_UPDATE_PATH)
    );
  }

  function getRequestUrl(input) {
    if (typeof input === 'string') {
      return input;
    }

    if (input instanceof URL) {
      return input.href;
    }

    if (input && typeof input.url === 'string') {
      return input.url;
    }

    return '';
  }

  function canUseResponseCart(responseUrl) {
    return responseUrl.includes(CART_CHANGE_PATH) || responseUrl.includes(CART_UPDATE_PATH);
  }

  async function getCartFromResponse(response, responseUrl) {
    if (!response || typeof response.clone !== 'function' || !canUseResponseCart(responseUrl)) {
      return null;
    }

    try {
      const responseCart = await response.clone().json();

      if (Array.isArray(responseCart?.items)) {
        return responseCart;
      }
    } catch (error) {
      console.warn('Collection Service Cart Snapshot Error:', error);
    }

    return null;
  }

  function patchWindowFetch() {
    if (globalScope.__belvansCollectionServiceFetchPatched) {
      return;
    }

    const originalFetch = globalScope.fetch;
    globalScope.__belvansCollectionServiceFetchPatched = true;

    globalScope.fetch = async function patchedFetch(...args) {
      const requestUrl = getRequestUrl(args[0]);
      const response = await originalFetch.apply(this, args);
      const responseUrl = response?.url || requestUrl;

      if (!isApplyingCartActions && shouldValidateCart(responseUrl || '')) {
        requestCartValidation(getCartFromResponse(response, responseUrl));
      }

      return response;
    };

    if (globalScope.window && globalScope.window.fetch !== globalScope.fetch) {
      globalScope.window.fetch = (...args) => globalScope.fetch(...args);
    }
  }

  const api = {
    SERVICE_FLAG_PROPERTY,
    SERVICE_ID_PROPERTY,
    SERVICE_FLAG_VALUE,
    buildCartLines,
    buildLineMetadataMap,
    computeCollectionServiceActions,
    computeDesiredServiceQuantities,
    getCartFromResponse,
    getRequestUrl,
    isCollectionServiceLine,
    lineMatchesRule,
    normalizeRules,
    requestCartValidation,
    shouldValidateCart,
    splitCollectionHandles,
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }

  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    globalScope.BelvansCollectionServiceCartValidator = api;

    document.addEventListener('DOMContentLoaded', () => {
      requestCartValidation();
    });
    document.addEventListener(CART_REFRESH_EVENT, (event) => requestCartValidation(Promise.resolve(event?.detail?.cart || null)));
    patchWindowFetch();
  }
})(typeof window !== 'undefined' ? window : globalThis);
